import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Phone, Star, MoreVertical, UserPlus, Search,
  Eye, Ban, RefreshCw, CheckCircle, XCircle, Download, Filter,
  Wallet, HandCoins, ToggleLeft, ToggleRight, Power,
  Navigation, MapPin, ArrowRight
} from 'lucide-react';
import { getDrivers, updateDriverStatus, updateDriverPermit } from '../../services/api';
import useWebSocket from '../../hooks/useWebSocket';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import AddDriverModal from './AddDriverModal';
import CashHandoverModal from './CashHandoverModal';
import { toast } from 'sonner';
import { formatCurrency, formatEuro, lekToEuro } from '../../utils/formatters';

const statusLabels = {
  online: 'Online',
  busy: 'Në Udhëtim',
  offline: 'Offline',
  suspended: 'I Pezulluar',
  pending: 'Në Pritje'
};

const statusStyles = {
  online: 'bg-green-100 text-green-700 border-green-200',
  busy: 'bg-yellow-100 text-yellow-700 border-yellow-200',
  offline: 'bg-gray-100 text-gray-600 border-gray-200',
  suspended: 'bg-red-100 text-red-700 border-red-200',
  pending: 'bg-orange-100 text-orange-700 border-orange-200'
};

// Animated Trip Progress Component
const TripProgressBadge = ({ origin, destination, progress = 0, startTime }) => {
  const [currentProgress, setCurrentProgress] = useState(progress);
  
  // Simulate progress animation
  useEffect(() => {
    if (startTime) {
      const elapsed = (Date.now() - new Date(startTime).getTime()) / 1000 / 60; // minutes
      const estimatedProgress = Math.min(95, elapsed * 5); // ~5% per minute, max 95%
      setCurrentProgress(estimatedProgress);
    }
    
    const interval = setInterval(() => {
      setCurrentProgress(prev => {
        if (prev >= 95) return prev;
        return prev + 0.5;
      });
    }, 3000);
    
    return () => clearInterval(interval);
  }, [startTime]);

  return (
    <div className="flex flex-col gap-1.5 min-w-[200px]">
      {/* Route Display */}
      <div className="flex items-center gap-1.5 text-xs">
        <div className="flex items-center gap-1 text-gray-600 max-w-[90px] truncate" title={origin}>
          <MapPin className="w-3 h-3 text-green-500 flex-shrink-0" />
          <span className="truncate">{origin || 'Nisja'}</span>
        </div>
        <ArrowRight className="w-3 h-3 text-gray-400 flex-shrink-0 animate-pulse" />
        <div className="flex items-center gap-1 text-gray-600 max-w-[90px] truncate" title={destination}>
          <Navigation className="w-3 h-3 text-red-500 flex-shrink-0" />
          <span className="truncate">{destination || 'Destinacioni'}</span>
        </div>
      </div>
      
      {/* Animated Progress Bar */}
      <div className="relative h-2 bg-gray-200 rounded-full overflow-hidden">
        {/* Background animation */}
        <div className="absolute inset-0 bg-gradient-to-r from-yellow-200 via-yellow-100 to-yellow-200 animate-pulse" />
        
        {/* Progress fill */}
        <div 
          className="absolute h-full bg-gradient-to-r from-yellow-400 to-yellow-500 rounded-full transition-all duration-1000 ease-out"
          style={{ width: `${currentProgress}%` }}
        >
          {/* Moving car indicator */}
          <div 
            className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-1/2 w-3 h-3 bg-yellow-600 rounded-full shadow-lg flex items-center justify-center"
          >
            <div className="w-1.5 h-1.5 bg-white rounded-full" />
          </div>
        </div>
        
        {/* Shimmer effect */}
        <div 
          className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent"
          style={{
            animation: 'shimmer 2s infinite',
            transform: 'translateX(-100%)',
          }}
        />
      </div>
      
      {/* CSS for shimmer animation */}
      <style>{`
        @keyframes shimmer {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
      `}</style>
    </div>
  );
};

export const DriversList = ({ onSelectDriver }) => {
  const navigate = useNavigate();
  const [drivers, setDrivers] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showCashHandoverModal, setShowCashHandoverModal] = useState(false);
  const [cashHandoverDriver, setCashHandoverDriver] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusLoading, setStatusLoading] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  const fetchDrivers = async () => {
    try {
      setLoading(true);
      const data = await getDrivers();
      setDrivers(data);
    } catch (error) {
      console.error('Error fetching drivers:', error);
    } finally {
      setLoading(false);
    }
  };

  // WebSocket connection for real-time updates
  const { isConnected } = useWebSocket('dispatcher', (message) => {
    console.log('DriversList received WebSocket message:', message);
    
    // Handle different message types for LIVE updates
    if (message.type === 'driver_status_changed') {
      // Update driver status in real-time without full refresh
      setDrivers(prev => prev.map(driver => 
        driver.id === message.driver_id 
          ? { ...driver, status: message.status }
          : driver
      ));
      toast.info(`🔄 Shoferi ndryshoi statusin në ${statusLabels[message.status] || message.status}`);
    } else if (message.type === 'driver_moved' || message.type === 'driver_location') {
      // Update driver position in real-time
      setDrivers(prev => prev.map(driver => 
        driver.id === message.driver_id 
          ? { ...driver, position: message.position }
          : driver
      ));
    } else if (message.type === 'driver_updated') {
      // Update driver info in real-time
      if (message.driver) {
        setDrivers(prev => prev.map(driver => 
          driver.id === message.driver_id 
            ? { ...driver, ...message.driver }
            : driver
        ));
      }
    } else if (message.type === 'new_driver') {
      // Fetch full list when new driver added
      fetchDrivers();
      toast.success('🆕 Shofer i ri u shtua!');
    } else if (message.type === 'driver_wallet_updated') {
      // Update wallet balance in real-time
      setDrivers(prev => prev.map(driver => 
        driver.id === message.driver_id 
          ? { ...driver, wallet_balance: message.wallet_balance }
          : driver
      ));
    } else if (message.type === 'driver_earnings_updated') {
      // Update earnings in real-time when ride completes
      setDrivers(prev => prev.map(driver => 
        driver.id === message.driver_id 
          ? { 
              ...driver, 
              daily_earnings: message.daily_earnings,
              total_earnings: message.total_earnings,
              total_rides: message.total_rides
            }
          : driver
      ));
      toast.success(`💰 ${message.driver_name}: +${formatCurrency(message.last_ride_amount * 100 || 0)} (${message.payment_method === 'cash' ? 'Cash' : 'Kartë'})`);
    } else if (message.type === 'ride_completed' || message.type === 'ride_started' || message.type === 'airport_ride_completed') {
      // Refresh on ride events
      fetchDrivers();
    } else if (message.type === 'ride_assigned' || message.type === 'ride_accepted') {
      // Update driver with current ride info
      if (message.driver_id && message.ride) {
        setDrivers(prev => prev.map(driver => 
          driver.id === message.driver_id 
            ? { 
                ...driver, 
                status: 'busy',
                current_ride: message.ride 
              }
            : driver
        ));
      } else {
        fetchDrivers();
      }
    } else if (message.type === 'trip_progress_update') {
      // Update trip progress in real-time
      setDrivers(prev => prev.map(driver => 
        driver.id === message.driver_id 
          ? { 
              ...driver, 
              current_ride: {
                ...driver.current_ride,
                progress: message.progress
              }
            }
          : driver
      ));
    }
  });

  useEffect(() => {
    fetchDrivers();
    
    // Fast polling for LIVE updates (every 3 seconds)
    // Works in background without any indicator
    const pollInterval = setInterval(() => {
      fetchDrivers();
    }, 3000);
    
    return () => clearInterval(pollInterval);
  }, []);

  const filteredDrivers = drivers.filter(driver => {
    const matchesSearch = driver.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         driver.phone?.includes(searchQuery) ||
                         driver.id?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === 'all' || driver.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const handleViewProfile = (driver) => {
    navigate(`/admin/drivers/${driver.id}/profile`);
  };

  const handleSuspendDriver = async (driver, e) => {
    e?.stopPropagation();
    try {
      await updateDriverStatus(driver.id, 'offline');
      toast.success(`Shoferi ${driver.name} u pezullua`);
      fetchDrivers();
    } catch (error) {
      toast.error('Gabim gjatë pezullimit');
    }
  };

  const handleActivateDriver = async (driver, e) => {
    e?.stopPropagation();
    try {
      await updateDriverStatus(driver.id, 'online');
      toast.success(`Shoferi ${driver.name} u aktivizua`);
      fetchDrivers();
    } catch (error) {
      toast.error('Gabim gjatë aktivizimit');
    }
  };

  const handleApprovePermit = async (driver, e) => {
    e?.stopPropagation();
    try {
      await updateDriverPermit(driver.id, 'approved');
      toast.success(`Leja e ${driver.name} u miratua`);
      fetchDrivers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gabim gjatë miratimit');
    }
  };

  const handleRejectPermit = async (driver, e) => {
    e?.stopPropagation();
    if (!window.confirm(`Refuzoni aplikimin e ${driver.name}?`)) return;
    try {
      await updateDriverPermit(driver.id, 'rejected');
      toast.success(`Aplikimi i ${driver.name} u refuzua`);
      fetchDrivers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gabim gjatë refuzimit');
    }
  };

  const handleToggleStatus = async (driver, e) => {
    e?.stopPropagation();
    const newStatus = driver.status === 'online' ? 'offline' : 'online';
    setStatusLoading(prev => ({ ...prev, [driver.id]: true }));
    try {
      await updateDriverStatus(driver.id, newStatus);
      toast.success(`Shoferi ${driver.name} u ndryshua në ${newStatus === 'online' ? 'Online' : 'Offline'}`);
      fetchDrivers();
    } catch (error) {
      toast.error('Gabim gjatë ndryshimit të statusit');
    } finally {
      setStatusLoading(prev => ({ ...prev, [driver.id]: false }));
    }
  };

  const handleOpenCashHandover = (driver, e) => {
    e?.stopPropagation();
    setCashHandoverDriver(driver);
    setShowCashHandoverModal(true);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('sq-AL', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      timeZone: 'Europe/Tirane'
    });
  };

  const onlineCount = drivers.filter(d => d.status === 'online').length;
  const busyCount = drivers.filter(d => d.status === 'busy').length;
  const offlineCount = drivers.filter(d => d.status === 'offline').length;

  return (
    <>
      <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden w-full">
        {/* Header */}
        <div className="p-6 border-b border-gray-100">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>
                Menaxhimi i Shoferëve
              </h2>
              <p className="text-sm text-gray-500 mt-1">Shiko, shto dhe menaxho të gjithë shoferët</p>
            </div>
            <div className="flex items-center gap-3">
              <button 
                onClick={fetchDrivers}
                className="p-2.5 hover:bg-gray-100 rounded-xl border border-gray-200"
                disabled={loading}
                title="Rifresko"
              >
                <RefreshCw className={`w-5 h-5 text-gray-500 ${loading ? 'animate-spin' : ''}`} />
              </button>
              <button 
                className="flex items-center gap-2 px-4 py-2.5 rounded-xl border border-gray-200 text-gray-700 hover:bg-gray-50"
              >
                <Download className="w-4 h-4" />
                Eksporto
              </button>
              <button 
                onClick={() => setShowAddModal(true)}
                className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-white font-semibold transition-all hover:scale-105 shadow-lg"
                style={{ background: 'linear-gradient(135deg, #dc2313, #b91c1c)' }}
              >
                <UserPlus className="w-5 h-5" />
                Shto Shofer të Ri
              </button>
            </div>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-4 gap-4 mb-6">
            <div className="p-4 rounded-xl bg-gradient-to-br from-gray-50 to-gray-100 border border-gray-200">
              <p className="text-sm text-gray-500 mb-1">Total Shoferë</p>
              <p className="text-3xl font-bold">{drivers.length}</p>
            </div>
            <div className="p-4 rounded-xl bg-gradient-to-br from-green-50 to-green-100 border border-green-200">
              <p className="text-sm text-green-600 mb-1">Online</p>
              <p className="text-3xl font-bold text-green-700">{onlineCount}</p>
            </div>
            <div className="p-4 rounded-xl bg-gradient-to-br from-yellow-50 to-yellow-100 border border-yellow-200">
              <p className="text-sm text-yellow-600 mb-1">Në Udhëtim</p>
              <p className="text-3xl font-bold text-yellow-700">{busyCount}</p>
            </div>
            <div className="p-4 rounded-xl bg-gradient-to-br from-gray-50 to-gray-100 border border-gray-200">
              <p className="text-sm text-gray-500 mb-1">Offline</p>
              <p className="text-3xl font-bold text-gray-600">{offlineCount}</p>
            </div>
          </div>

          {/* Search & Filter */}
          <div className="flex items-center gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Kërko sipas ID, emrit, ose numrit të telefonit..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-12 pr-4 py-3 rounded-xl border border-gray-200 text-sm focus:ring-2 focus:ring-red-400 focus:border-transparent"
              />
            </div>
            <div className="flex items-center gap-2">
              <Filter className="w-5 h-5 text-gray-400" />
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-4 py-3 rounded-xl border border-gray-200 text-sm focus:ring-2 focus:ring-red-400 bg-white"
              >
                <option value="all">Të gjithë statuset</option>
                <option value="online">Online</option>
                <option value="busy">Në Udhëtim</option>
                <option value="offline">Offline</option>
                <option value="suspended">I Pezulluar</option>
              </select>
            </div>
          </div>
        </div>

        {/* Drivers Table */}
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="text-left px-4 py-4 text-xs font-bold text-gray-500 uppercase tracking-wider">ID</th>
                <th className="text-left px-4 py-4 text-xs font-bold text-gray-500 uppercase tracking-wider">Emër Mbiemër</th>
                <th className="text-left px-4 py-4 text-xs font-bold text-gray-500 uppercase tracking-wider">Nr. Celular</th>
                <th className="text-center px-4 py-4 text-xs font-bold text-gray-500 uppercase tracking-wider">Online/Offline</th>
                <th className="text-center px-4 py-4 text-xs font-bold text-gray-500 uppercase tracking-wider">Xhiro Sot</th>
                <th className="text-center px-4 py-4 text-xs font-bold text-gray-500 uppercase tracking-wider">Xhiro Totale</th>
                <th className="text-center px-4 py-4 text-xs font-bold text-gray-500 uppercase tracking-wider">Vlerësimi</th>
                <th className="text-center px-4 py-4 text-xs font-bold text-gray-500 uppercase tracking-wider">Udhëtime</th>
                <th className="text-center px-4 py-4 text-xs font-bold text-gray-500 uppercase tracking-wider">Veprime</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading && drivers.length === 0 ? (
                <tr>
                  <td colSpan="9" className="text-center py-12 text-gray-500">
                    <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-3 text-gray-300" />
                    Duke ngarkuar shoferët...
                  </td>
                </tr>
              ) : filteredDrivers.length === 0 ? (
                <tr>
                  <td colSpan="9" className="text-center py-12 text-gray-500">
                    Nuk u gjetën shoferë me këto kritere
                  </td>
                </tr>
              ) : (
                filteredDrivers.map((driver, index) => (
                  <tr 
                    key={driver.id} 
                    className="hover:bg-gray-50 cursor-pointer transition-colors"
                    onClick={() => handleViewProfile(driver)}
                  >
                    {/* ID */}
                    <td className="px-4 py-4">
                      <span className="font-mono text-sm text-gray-500">
                        #{(index + 1).toString().padStart(3, '0')}
                      </span>
                    </td>

                    {/* Emër Mbiemër */}
                    <td className="px-4 py-4">
                      <div className="flex items-center gap-3">
                        <div 
                          className="w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold"
                          style={{ background: 'linear-gradient(135deg, #fabc0c, #e0a80a)', color: '#1b1c1b' }}
                        >
                          {driver.name?.substring(0, 2).toUpperCase()}
                        </div>
                        <div>
                          <p className="font-semibold text-gray-900">{driver.name}</p>
                        </div>
                      </div>
                    </td>

                    {/* Nr. Celular */}
                    <td className="px-4 py-4">
                      <a 
                        href={`tel:${driver.phone}`}
                        onClick={(e) => e.stopPropagation()}
                        className="flex items-center gap-2 text-sm text-gray-700 hover:text-red-600"
                      >
                        <Phone className="w-4 h-4" />
                        {driver.phone}
                      </a>
                    </td>

                    {/* Online/Offline Toggle with Trip Progress */}
                    <td className="px-4 py-4" onClick={(e) => e.stopPropagation()}>
                      {driver.status === 'busy' ? (
                        <div className="flex flex-col items-center gap-2">
                          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-yellow-100 text-yellow-700">
                            <Power className="w-3.5 h-3.5" />
                            Në Udhëtim
                          </span>
                          <TripProgressBadge 
                            origin={driver.current_ride?.pickup_location || driver.current_trip?.origin || 'Në rrugë'}
                            destination={driver.current_ride?.dropoff_location || driver.current_trip?.destination || 'Destinacion'}
                            startTime={driver.current_ride?.started_at || driver.current_trip?.started_at}
                            progress={driver.current_ride?.progress || 30}
                          />
                        </div>
                      ) : (
                        <div className="flex justify-center">
                          <button
                            onClick={(e) => handleToggleStatus(driver, e)}
                            disabled={statusLoading[driver.id]}
                            className={`relative inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold transition-all ${
                              driver.status === 'online'
                              ? 'bg-green-100 text-green-700 hover:bg-green-200'
                              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                            }`}
                            title="Ndrysho statusin"
                          >
                            {statusLoading[driver.id] ? (
                              <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                            ) : driver.status === 'online' ? (
                              <ToggleRight className="w-5 h-5" />
                            ) : (
                              <ToggleLeft className="w-5 h-5" />
                            )}
                            {statusLabels[driver.status] || 'Offline'}
                          </button>
                        </div>
                      )}
                    </td>

                    {/* Xhiro Sot (Today's Earnings) */}
                    <td className="px-4 py-4 text-center" onClick={(e) => e.stopPropagation()}>
                      <div className="flex flex-col items-center gap-1">
                        <div className="flex items-center gap-1 text-sm font-bold text-gray-800">
                          <Wallet className="w-4 h-4 text-amber-500" />
                          {formatCurrency((driver.daily_earnings || 0) * 100)}
                        </div>
                        {(driver.daily_earnings || 0) > 0 && (
                          <div className="text-xs text-gray-500">
                            {formatEuro(lekToEuro((driver.daily_earnings || 0) * 100))}
                          </div>
                        )}
                        {(driver.daily_earnings || 0) > 0 && (
                          <button
                            onClick={(e) => handleOpenCashHandover(driver, e)}
                            className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-green-700 bg-green-50 rounded-lg hover:bg-green-100 transition-colors"
                          >
                            <HandCoins className="w-3.5 h-3.5" />
                            Dorëzo
                          </button>
                        )}
                      </div>
                    </td>

                    {/* Xhiro Totale (Total Earnings) */}
                    <td className="px-4 py-4 text-center">
                      <div className="flex flex-col items-center">
                        <span className="text-sm font-bold text-green-600">
                          {formatCurrency((driver.total_earnings || 0) * 100)}
                        </span>
                        {(driver.total_earnings || 0) > 0 && (
                          <span className="text-xs text-gray-500">
                            {formatEuro(lekToEuro((driver.total_earnings || 0) * 100))}
                          </span>
                        )}
                      </div>
                    </td>

                    {/* Vlerësimi */}
                    <td className="px-4 py-4 text-center">
                      <div className="flex items-center justify-center gap-1">
                        <Star className="w-4 h-4 text-yellow-400 fill-current" />
                        <span className="font-bold text-gray-800">{driver.rating}</span>
                      </div>
                    </td>

                    {/* Udhëtime */}
                    <td className="px-4 py-4 text-center">
                      <span className="font-semibold text-gray-800">{driver.total_rides}</span>
                    </td>

                    {/* Veprime */}
                    <td className="px-4 py-4 text-center" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center justify-center gap-2">
                        <button
                          onClick={() => handleViewProfile(driver)}
                          className="p-2 rounded-lg hover:bg-gray-100 text-gray-500 hover:text-gray-700"
                          title="Shiko Profilin"
                        >
                          <Eye className="w-5 h-5" />
                        </button>
                        <DropdownMenu>
                          <DropdownMenuTrigger className="p-2 rounded-lg hover:bg-gray-100 text-gray-500 hover:text-gray-700">
                            <MoreVertical className="w-5 h-5" />
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="w-52">
                            <DropdownMenuItem onClick={() => handleViewProfile(driver)}>
                              <Eye className="w-4 h-4 mr-2" /> Shiko Profilin e Plotë
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => window.open(`tel:${driver.phone}`)}>
                              <Phone className="w-4 h-4 mr-2" /> Telefono
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            {(driver.wallet_balance || 0) > 0 && (
                              <DropdownMenuItem 
                                className="text-green-600"
                                onClick={(e) => handleOpenCashHandover(driver, e)}
                              >
                                <HandCoins className="w-4 h-4 mr-2" /> Dorëzo Xhiron
                              </DropdownMenuItem>
                            )}
                            {driver.permit_status === 'pending' && (
                              <>
                                <DropdownMenuItem
                                  className="text-green-600"
                                  onClick={(e) => handleApprovePermit(driver, e)}
                                >
                                  <CheckCircle className="w-4 h-4 mr-2" /> Aprovo Lejen
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                  className="text-red-600"
                                  onClick={(e) => handleRejectPermit(driver, e)}
                                >
                                  <XCircle className="w-4 h-4 mr-2" /> Refuzo Lejen
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                              </>
                            )}
                            {driver.status === 'offline' || driver.status === 'suspended' ? (
                              <DropdownMenuItem 
                                className="text-green-600"
                                onClick={(e) => handleActivateDriver(driver, e)}
                              >
                                <CheckCircle className="w-4 h-4 mr-2" /> Aktivizo Shoferin
                              </DropdownMenuItem>
                            ) : driver.status !== 'busy' && (
                              <DropdownMenuItem 
                                className="text-red-600"
                                onClick={(e) => handleSuspendDriver(driver, e)}
                              >
                                <Ban className="w-4 h-4 mr-2" /> Pezullo Shoferin
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-between bg-gray-50">
          <p className="text-sm text-gray-500">
            Duke shfaqur <span className="font-semibold">{filteredDrivers.length}</span> nga <span className="font-semibold">{drivers.length}</span> shoferë
          </p>
          <div className="flex items-center gap-2">
            <button className="px-4 py-2 rounded-lg border border-gray-200 text-sm text-gray-600 hover:bg-white">
              ← Para
            </button>
            <button className="px-4 py-2 rounded-lg border border-gray-200 text-sm text-gray-600 hover:bg-white">
              Pas →
            </button>
          </div>
        </div>
      </div>

      {/* Add Driver Modal */}
      <AddDriverModal 
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onSuccess={() => {
          fetchDrivers();
          setShowAddModal(false);
        }}
      />

      {/* Cash Handover Modal */}
      <CashHandoverModal
        isOpen={showCashHandoverModal}
        onClose={() => {
          setShowCashHandoverModal(false);
          setCashHandoverDriver(null);
        }}
        driver={cashHandoverDriver}
        onSuccess={() => {
          fetchDrivers();
          setShowCashHandoverModal(false);
          setCashHandoverDriver(null);
        }}
      />
    </>
  );
};

export default DriversList;
